import type { Itinerary, AgentEvent } from '../types';

/** Picsum gives a real https:// URL that DreamCardPanel can display */
const MOCK_POSTCARD_URL = 'https://picsum.photos/seed/barcelona-dream/800/600';

export const MOCK_ITINERARY: Itinerary = {
  sessionId: 'mock-session',
  status: 'COMPLETE',
  postcardUrl: MOCK_POSTCARD_URL,
  itinerary: {
    title: 'Dream Getaway to Barcelona',
    summary: 'A 3-day adventure through the vibrant streets of Barcelona, blending culture, cuisine, and coastal charm.',
    flightDetails: {
      departure: 'London Heathrow (LHR)',
      arrival: 'Barcelona El Prat (BCN)',
      suggestedFlight: 'BA478 — 09:15 → 12:30',
    },
    accommodationDetails: {
      name: 'Hotel Casa Bonay',
      type: 'Boutique Hotel',
      description: 'A stylish boutique hotel in the heart of Eixample, walking distance to major attractions.',
    },
    days: [
      {
        day: 1,
        theme: 'Gothic Quarter & Culture',
        activities: [
          { time: '10:00', activity: 'Explore the Gothic Quarter', description: 'Wander through medieval streets and visit the Barcelona Cathedral.', location: 'Barri Gòtic' },
          { time: '13:00', activity: 'Lunch at La Boqueria', description: 'Fresh tapas and local produce at the famous market.', location: 'La Rambla', cost: '€20' },
          { time: '15:00', activity: 'Picasso Museum', description: 'Discover Picasso\'s early works in a stunning medieval palace.', location: 'El Born', cost: '€12' },
        ],
      },
      {
        day: 2,
        theme: 'Gaudí & Modernisme',
        activities: [
          { time: '09:00', activity: 'Sagrada Família', description: 'Marvel at Gaudí\'s unfinished masterpiece.', location: 'Eixample', cost: '€26' },
          { time: '12:00', activity: 'Park Güell', description: 'Colourful mosaics and panoramic city views.', location: 'Gràcia', cost: '€10' },
          { time: '15:00', activity: 'Casa Batlló', description: 'Explore the fantastical façade and interior.', location: 'Passeig de Gràcia', cost: '€35' },
        ],
      },
      {
        day: 3,
        theme: 'Beach & Farewell',
        activities: [
          { time: '09:00', activity: 'Barceloneta Beach', description: 'Morning swim and coffee by the sea.', location: 'Barceloneta' },
          { time: '12:00', activity: 'Seafood lunch', description: 'Paella with a view at a beachfront restaurant.', location: 'Port Olímpic', cost: '€30' },
          { time: '15:00', activity: 'Montjuïc', description: 'Cable car ride and sunset views before heading to the airport.', location: 'Montjuïc', cost: '€13' },
        ],
      },
    ],
    costBreakdown: [
      { category: 'Flights', amount: '€180' },
      { category: 'Accommodation (3 nights)', amount: '€450' },
      { category: 'Activities', amount: '€96' },
      { category: 'Food & Drink', amount: '€200' },
      { category: 'Transport', amount: '€50' },
      { category: 'TOTAL', amount: '€976' },
    ],
    tips: [
      'Book Sagrada Família tickets online in advance — they sell out fast.',
      'Get a T-Casual transport card for 10 metro/bus rides.',
      'Dinner in Barcelona starts around 21:00 — embrace the late schedule.',
    ],
  },
};

const MOCK_EVENT_SEQUENCE: Omit<AgentEvent, 'sessionId' | 'timestamp'>[] = [
  { eventType: 'AGENT_START', message: 'Starting itinerary generation' },
  { eventType: 'THINKING', message: 'Let me analyze the destination and travel dates to find the best options. I need to consider flights, accommodation, and local activities that match the budget.' },
  { eventType: 'TOOL_START', message: 'Searching for the best flights', toolName: 'flight_search' },
  { eventType: 'TOOL_END', message: 'Found flights', toolName: 'flight_search' },
  { eventType: 'THINKING', message: 'Good, I found several flight options. The morning departure looks best — it maximizes time at the destination. Now let me find accommodation nearby.' },
  { eventType: 'TOOL_START', message: 'Finding the perfect hotel', toolName: 'hotel_search' },
  { eventType: 'TOOL_END', message: 'Found a great hotel', toolName: 'hotel_search' },
  { eventType: 'THINKING', message: 'Hotel Casa Bonay in Eixample is a great fit — central location, within budget, and highly rated. Let me now look for activities that blend culture and local experiences.' },
  { eventType: 'TOOL_START', message: 'Discovering activities', toolName: 'activities_finder' },
  { eventType: 'TOOL_END', message: 'Found some cool activities', toolName: 'activities_finder' },
  { eventType: 'TOOL_START', message: 'Checking the weather', toolName: 'weather_lookup' },
  { eventType: 'TOOL_END', message: 'Weather looks great!', toolName: 'weather_lookup' },
  { eventType: 'THINKING', message: 'Weather is sunny and warm — perfect for outdoor activities. I\'ll structure the days to balance indoor museums with outdoor exploration and beach time.' },
  { eventType: 'AGENT_TEXT', message: 'Building your itinerary...' },
  { eventType: 'AGENT_COMPLETE', message: 'Itinerary ready!' },
];

/** Returns all events at once (for initial state). */
export function createMockEvents(sessionId: string): AgentEvent[] {
  const now = new Date().toISOString();
  return MOCK_EVENT_SEQUENCE.map(e => ({ ...e, sessionId, timestamp: now }));
}

/**
 * Streams mock events one-by-one with delays for a realistic generating screen.
 * Returns a cleanup function to cancel pending timers.
 */
export function streamMockEvents(
  sessionId: string,
  onEvent: (events: AgentEvent[]) => void,
  onComplete: () => void,
): () => void {
  const timers: ReturnType<typeof setTimeout>[] = [];
  const accumulated: AgentEvent[] = [];
  const delayMs = 400; // ms between events

  MOCK_EVENT_SEQUENCE.forEach((evt, i) => {
    const timer = setTimeout(() => {
      accumulated.push({ ...evt, sessionId, timestamp: new Date().toISOString() });
      onEvent([...accumulated]);
      if (i === MOCK_EVENT_SEQUENCE.length - 1) onComplete();
    }, delayMs * (i + 1));
    timers.push(timer);
  });

  return () => timers.forEach(clearTimeout);
}
